export const getTimeFromTimeStamp = (value) => {
  value = new Date(value);
  let hours = value.getHours();
  let minutes = value.getMinutes() == 0 ? "00" : value.getMinutes();

  let ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

export const formatDateToString = (dateObject) => {
  if (!dateObject) return [];

  const month = dateObject.getUTCMonth() + 1;
  const day = dateObject.getUTCDate();
  const year = dateObject.getUTCFullYear();

  const strMonth = String(month).padStart(2, "0");
  const strDay = String(day).padStart(2, "0");
  const strYear = String(year).padStart(2, "0");

  const formattedDateString = `${strYear}-${strMonth}-${strDay}`;
  const formattedDateObject = { name: formattedDateString };

  return [formattedDateObject];
};

export const getFormattedDate = (date) => {
  if (!date || isNaN(date)) return "";
  let year = date.getFullYear();
  let month = (1 + date.getMonth()).toString().padStart(2, "0");
  let day = date.getDate().toString().padStart(2, "0");

  return month + "/" + day + "/" + year;
};

// format 16 Jul 2024
export function formatDateToShort(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error("Invalid date");
  }
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date.toLocaleDateString("en-GB", options).replace(/ /g, " ");
}

//format 16:32
export function formatTimeToHHMM(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error("Invalid date");
  }
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

//format 08-02-2024_00:24
export function formatDateTimeWithSeconds(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error("Invalid date");
  }
  let MM = String(date.getMonth() + 1).padStart(2, "0");
  let dd = String(date.getDate()).padStart(2, "0");
  let yyyy = date.getFullYear();
  let hh = String(date.getHours()).padStart(2, "0");
  let mm = String(date.getMinutes()).padStart(2, "0");

  return `${MM}-${dd}-${yyyy}_${hh}:${mm}`;
}

// TAD-5796 — Conditional timezone adjustment for the Fluent DatePicker.
//
// History: the original helper added the local timezone offset to every
// Date unconditionally. That was written as a workaround for the legacy
// react-datepicker, which would display the previous calendar day in
// negative-UTC-offset timezones (e.g. Eastern Time) when the Date was
// stored as UTC midnight (the conventional "date-only" wire shape).
// Adding the offset shifted the underlying ms forward so the picker's
// local-component rendering landed on the intended day.
//
// Bug surfaced by QA on TAD-5641 / reported on TAD-5796: the
// adjustment also fires for dates whose UTC time is NOT midnight
// (e.g. when the .NET API serializes a DateTime with Kind=Unspecified
// as `"2024-03-15T23:00:00"`, JS treats the no-timezone string as
// local time, and the +offset shift then rolls the date forward across
// midnight). Result: the Edit Task picker displayed Due as the
// following day relative to the task list.
//
// Fix: only apply the legacy shift to dates that are exactly at UTC
// midnight (the original case the workaround was written for).
// Any other Date is treated as already-correct and returned unchanged.
// This preserves the legacy round-trip for UTC-midnight wire dates
// while leaving local-time or mid-day dates alone.
export const adjustDateForPicker = (date) => {
  if (!date) return null;
  const isUtcMidnight =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;
  if (!isUtcMidnight) return date;
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const DateFormatBasedOnDepartment = (date, dataTimeFormat) => {
  const parts = date.split("/");
  let day = dataTimeFormat === 1 ? parts[0] : parts[1];
  let month = dataTimeFormat === 1 ? parts[1] - 1 : parts[0] - 1;
  let year = parts[2];

  return new Date(year, month, day);
};

export const DateStringFormatBasedOnDepartment = (date, dataTimeFormat) => {
  if (!(date instanceof Date) || isNaN(date) || !date) {
    throw new Error("Invalid date");
  }
  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  let formattedDate =
    dataTimeFormat === 1
      ? `${day}/${month}/${year}`
      : `${month}/${day}/${year}`;

  return formattedDate;
};

export const setTimeToZero = (date) => {
  if (date instanceof Date && !isNaN(date)) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  return date;
};

export function addDays(date, days = 0) {
  if (!date || !(date instanceof Date)) return null;
  const copy = new Date(date.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}

//input 2025-02-13 20:53:25.7810855 (DateTime.UtcNow - API response)
//output February 13th, 2025 at 8:53 PM
export function formatDate(data) {
  const date = new Date(data);

  if (isNaN(date.getTime())) {
    console.error("Invalid date format");
    return;
  }

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
  const day = date.getDate();

  function getOrdinalSuffix(day) {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  return formattedDate.replace(/\d+/, day + getOrdinalSuffix(day));
}

//output Wed · Mar 12, 2025
export const formatDateWithDay = (date) => {
  if (isNaN(date.getTime())) {
    console.error("Invalid date format");
    return;
  }
  const options = {
    weekday: "short", // "Wed"
    month: "short", // "Mar"
    day: "numeric", // 12
    year: "numeric", // 2025
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);

  const parts = formattedDate.split(", ");
  const weekday = parts[0];
  const monthAndDay = parts[1];
  const year = parts[2];

  return `${weekday} · ${monthAndDay}, ${year}`;
};
